import React, { useState, useEffect } from 'react';
import BackButtonBottom from '../../components/common/BackButtonBottom';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

// ═══════════════════════════════════════════════════════════════
// MY RECHECK —” Driver page (Batch 16 M2)
// ═══════════════════════════════════════════════════════════════
// Shows annual recheck status. Allows driver to re-sign policy
// acknowledgement. Other items are admin-recorded.
// ═══════════════════════════════════════════════════════════════

const CURRENT_POLICY_VERSION = 'v1.0-2026-05-17';

export default function MyRecheck() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [acknowledging, setAcknowledging] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    if (!currentUser) return;
    const snap = await getDoc(doc(db, 'users', currentUser.uid));
    if (snap.exists()) setDriver({ id: snap.id, ...snap.data() });
  };

  useEffect(() => { load(); }, [currentUser?.uid]);

  const daysUntil = (date) => {
    if (!date) return null;
    const d = date.toDate ? date.toDate() : new Date(date);
    return Math.floor((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const fmtDate = (date) => {
    if (!date) return '—”';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    setError('');
    try {
      const callable = httpsCallable(functions, 'acknowledgePolicyRecheck');
      await callable({ policyVersion: CURRENT_POLICY_VERSION });
      await load();
    } catch (e) {
      setError(e.message || 'Acknowledgement failed');
    } finally {
      setAcknowledging(false);
    }
  };

  if (!driver) return <div className="p-10 text-center">Loading...</div>;

  const recheck = driver.recheck || {};
  const items = recheck.items || {};
  const days = daysUntil(recheck.nextDueDate);
  const policyAcked = items.policyAcknowledgement?.completed
    && items.policyAcknowledgement?.policyVersion === CURRENT_POLICY_VERSION;

  let status;
  if (driver.recheckBlocked) {
    status = { label: `Overdue by ${Math.abs(days)}d`, color: '#dc2626', bg: '#fee2e2' };
  } else if (days === null) {
    status = { label: 'Not scheduled', color: '#94a3b8', bg: '#f1f5f9' };
  } else if (days < 0) {
    status = { label: `Overdue by ${Math.abs(days)}d`, color: '#dc2626', bg: '#fee2e2' };
  } else if (days <= 30) {
    status = { label: `Due in ${days}d`, color: '#dc2626', bg: '#fee2e2' };
  } else if (days <= 60) {
    status = { label: `Due in ${days}d`, color: '#92400e', bg: '#fef3c7' };
  } else {
    status = { label: `Due in ${days}d`, color: '#059669', bg: 'rgba(45,212,168,0.1)' };
  }

  const itemList = [
    { key: 'criminalHistory', label: 'National Criminal History Check', driverActionable: false },
    { key: 'drivingRecord', label: 'Driving Record Check', driverActionable: false },
    { key: 'medicalFitness', label: 'Medical Fitness Review', driverActionable: false },
    { key: 'wwcc', label: 'Working With Children Check', driverActionable: false, conditional: true,
      onFile: !!driver.documents?.wwcc?.expiryDate },
    { key: 'policyAcknowledgement', label: 'Policy Acknowledgement', driverActionable: true },
  ];

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0 mb-4">← Back</button>

      <h1 className="font-serif mt-0 mb-2">Annual Recheck</h1>
      <p className="text-body text-[0.9rem] mb-4">
        Reservo is required to re-verify every Driver's eligibility annually.
        Your next recheck is shown below.
      </p>

      {/* Status card */}
      <div className="p-4 rounded-xl bg-white border border-slate-200 mb-5">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-[0.82rem] text-muted">Next recheck due</div>
            <div className="text-[1.1rem] font-bold text-brand-dark">
              {fmtDate(recheck.nextDueDate)}
            </div>
            {recheck.lastCompletedAt && (
              <div className="text-[0.78rem] text-muted mt-1">
                Last completed: {fmtDate(recheck.lastCompletedAt)}
              </div>
            )}
          </div>
          <span className="px-3.5 py-1.5 rounded-full text-[0.78rem] font-bold" style={{ background: status.bg, color: status.color }}>{status.label}</span>
        </div>
      </div>

      {driver.recheckBlocked && (
        <div className="p-3.5 rounded-xl mb-5" style={{ background: '#fee2e2', border: '1px solid #fecaca', color: '#7f1d1d' }}>
          <strong>Account blocked.</strong> Your annual recheck is overdue.
          Contact admin@reservo.au or 0435 679 630 to schedule.
        </div>
      )}

      <h2 className="text-[1rem] mt-0 mb-3 text-brand-dark">Recheck items</h2>

      <div className="grid gap-2.5">
        {itemList.map((item) => {
          if (item.conditional && !item.onFile) return null;
          const done = items[item.key]?.completed;
          const completedAt = items[item.key]?.completedAt;

          return (
            <div key={item.key} className="p-3.5 rounded-xl bg-white border border-slate-200">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-bold text-brand-dark">{item.label}</div>
                  <div className="text-[0.78rem] text-muted mt-0.5">
                    {item.driverActionable ? 'You complete this' : 'Reservo Admin records this'}
                  </div>
                  {done && completedAt && (
                    <div className="text-[0.78rem] mt-1" style={{ color: '#059669' }}>
                      ✓“ Completed {fmtDate(completedAt)}
                    </div>
                  )}
                </div>
                <span className="px-2.5 py-1 rounded-full text-[0.72rem] font-bold"
                  style={{
                    background: done ? 'rgba(45,212,168,0.1)' : '#fef3c7',
                    color: done ? '#059669' : '#92400e',
                  }}>
                  {done ? 'Complete' : 'Pending'}
                </span>
              </div>

              {item.key === 'policyAcknowledgement' && !policyAcked && (
                <div className="mt-2.5">
                  <p className="text-[0.85rem] text-body mb-2">
                    Confirm you have read and continue to abide by the Driver Code of Conduct,
                    Anti-Discrimination Policy, Vulnerable Passenger Policy, Child Safety Policy,
                    and Accessibility Policy (version {CURRENT_POLICY_VERSION}).
                  </p>
                  <button onClick={() => navigate('/compliance')} className="mr-2 px-3 py-1.5 rounded-lg border-[1.5px] border-slate-200 bg-white text-body cursor-pointer font-semibold text-[0.82rem]">Review policies</button>
                  <button onClick={handleAcknowledge} disabled={acknowledging} className="px-3 py-1.5 rounded-lg border-0 bg-brand-primary text-white cursor-pointer font-bold text-[0.82rem]">
                    {acknowledging ? 'Saving...' : 'I acknowledge'}
                  </button>
                  {error && <div className="text-[0.82rem] mt-1.5" style={{ color: '#dc2626' }}>{error}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-3.5 rounded-xl bg-slate-50 border border-slate-200 text-[0.82rem] text-body leading-relaxed">
        Reservo Admin schedules the criminal history, driving record, medical, and (if applicable) WWCC
        re-checks each year. You'll be contacted by phone or email when it's time.
        You can complete your policy acknowledgement above at any time.
      </div>
      <BackButtonBottom label="Back" />
    </div>
  );
}